import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { IMAGE_URLS , BASE_URL} from '../config/urls';

// Helper Functions
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
};

export const formatEstimatedTime = (timeString) => {
  if (!timeString) return 'N/A';
  
  // Assuming timeString is in ISO format or similar
  const date = new Date(timeString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Preview URL Service
const previewCache = new Map();

export const isUrlValid = (cacheEntry) => {
  const validityPeriod = 90 * 60 * 1000; // 1.5 hours in milliseconds
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < validityPeriod;
};

export const getPreviewUrl = async (imageId) => {
  const getToken = () => localStorage.getItem('token');
  
  const cachedEntry = previewCache.get(imageId);
  if (isUrlValid(cachedEntry)) {
    return cachedEntry.url;
  }
  
  try {
    const response = await axios.get(IMAGE_URLS.getPreview(imageId), {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    
    if (response.data.status === "success") {
      const previewUrl = response.data.preview_url;
      
      previewCache.set(imageId, {
        url: previewUrl,
        timestamp: Date.now()
      });
      
      return previewUrl;
    }
    
    throw new Error("Failed to get preview URL");
  } catch (error) {
    console.error(`Error fetching preview for image ${imageId}:`, error);
    throw error;
  }
};

export const prefetchPreviews = async (imageIds) => {
  const uniqueIds = [...new Set(imageIds)];
  const idsToFetch = uniqueIds.filter(id => !isUrlValid(previewCache.get(id)));
  
  const fetchPromises = idsToFetch.map(id => getPreviewUrl(id).catch(() => null));
  await Promise.allSettled(fetchPromises);
};

export const cleanupPreviewCache = () => {
  for (const [imageId, entry] of previewCache.entries()) {
    if (!isUrlValid(entry)) {
      previewCache.delete(imageId);
    }
  }
};

// Run cleanup periodically
setInterval(cleanupPreviewCache, 30 * 60 * 1000); // Every 30 minutes

// TableRowImage component for showing preview thumbnails in the table
export const TableRowImage = ({ imageId, fallbackUrl }) => {
  const [previewUrl, setPreviewUrl] = useState(fallbackUrl);
  const [isLoading, setIsLoading] = useState(!fallbackUrl); // if fallback exists, don't show loading

  useEffect(() => {
    // console.log(imageId)
    if (!fallbackUrl && imageId) {
      const fetchFreshPreviewUrl = async () => {
        try {
          setIsLoading(true);

          const url = imageId.previewUrl
          setPreviewUrl(url);
        } catch (err) {
          console.error("Error fetching preview URL for thumbnail:", err);
        } finally {
          setIsLoading(false);
        }
      };

      fetchFreshPreviewUrl();
    }
  }, [imageId, fallbackUrl]); // notice: fallbackUrl also added to deps

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      {isLoading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: 60, 
            height: 60, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            borderRadius: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.1)'
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <img 
        src={previewUrl} 
        alt="Preview"
        style={{ 
          width: 60, 
          height: 60, 
          objectFit: 'cover',
          borderRadius: 4
        }}
        onError={(e) => {
          e.target.src = "/api/placeholder/60/60";
        }}
      />
    </Box>
  );
};


// API service for catalog orders
export const useCatalogOrder = () => {
  const API_BASE_URL = BASE_URL+'/api';
  
  const getToken = () => localStorage.getItem('token');
  
  const getOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/catalog-orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      return response.data;
    } catch (err) {
      console.error('Error fetching order details:', err);
      throw err;
    }
  };

  const getAllOrders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/catalog-orders`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      return response.data;
    } catch (err) {
      console.error('Error fetching orders:', err);
      throw err;
    }
  };

  const downloadDataProduct = async (orderId, productId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/catalog-orders/${orderId}/products/${productId}/download`, 
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `product-${productId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return true;
    } catch (err) {
      console.error('Error downloading product:', err);
      throw err;
    }
  };

  return {
    getOrderDetails,
    getAllOrders,
    downloadDataProduct
  };
};